<template>
  <ApiResourceCard :name="integration.name">
    <template #actions>
      <ApiResourceCardActions
        :action-text="canManage ? 'Settings' : ''"
        :action-icon="['fak', 'settings']"
        :resource-name="integration.name"
        :menu-options="menuOptions"
        @action="emit('settings')"
        @menu-select="handleMenuSelect"
      />
    </template>
    <ApiMetaList>
      <ApiMetaItem
        v-for="item in metaItems"
        :key="item.label"
        :label="item.label"
        :value="item.value"
      >
        <ApiCopyValue
          v-if="item.copyLabel"
          :value="item.value"
          :label="item.copyLabel"
        />
      </ApiMetaItem>
    </ApiMetaList>
  </ApiResourceCard>
</template>

<script setup lang="ts">
import { DateTime } from 'luxon';
import { computed } from 'vue';

import { useFlashesStore } from '@/stores';
import type { OauthApplication } from '@/types/oauth_applications';
import { copyToClipBoard } from '@/utils';

import type { ApiCardMenuOptionType } from '../types';

import ApiCopyValue from './ApiCopyValue.vue';
import ApiMetaItem from './ApiMetaItem.vue';
import ApiMetaList from './ApiMetaList.vue';
import ApiResourceCard from './ApiResourceCard.vue';
import ApiResourceCardActions from './ApiResourceCardActions.vue';

const { integration, canManage = false } = defineProps<{
  integration: OauthApplication;
  canManage?: boolean;
}>();

const emit = defineEmits<{
  settings: [];
  delete: [];
}>();

const COPY_CLIENT_ID_OPTION: ApiCardMenuOptionType = {
  label: 'Copy client ID',
  value: 'copy-client-id',
  faIcon: ['fak', 'copy'],
};

const DELETE_OPTION: ApiCardMenuOptionType = {
  label: 'Delete integration',
  value: 'delete',
  faIcon: ['fas', 'trash-can'],
};

const menuOptions = computed(() =>
  canManage ? [COPY_CLIENT_ID_OPTION, DELETE_OPTION] : [COPY_CLIENT_ID_OPTION],
);

const flashesStore = useFlashesStore();

const createdLabel = computed(() =>
  DateTime.fromISO(integration.created_at).toLocaleString(DateTime.DATE_MED),
);

const lastUsedLabel = computed(() => {
  if (!integration.last_used_at) return 'Never';
  const relative = DateTime.fromISO(integration.last_used_at).toRelative();
  return !relative || relative.startsWith('in') ? 'Just now' : relative;
});

const callbacksLabel = computed(() =>
  integration.callbacks.length > 1 ? 'Callback URLs' : 'Callback URL',
);

const callbacksValue = computed(
  () => integration.callbacks.join(', ') || 'None',
);

const metaItems = computed(() => [
  {
    label: 'Client ID',
    value: integration.client_id,
    copyLabel: `client ID for ${integration.name}`,
  },
  { label: 'Created', value: createdLabel.value },
  { label: 'Last used', value: lastUsedLabel.value },
  { label: callbacksLabel.value, value: callbacksValue.value },
]);

async function handleMenuSelect(value: string) {
  if (value === 'delete') {
    emit('delete');
    return;
  }
  await copyToClipBoard(integration.client_id);
  flashesStore.addSuccessFlash({ message: 'Client ID copied to clipboard' });
}
</script>
